<template>
	<div class="moments-stories-player">
		<div
			ref="timeline"
			class="moments-stories-player__timeline"
			aria-hidden="true"
			hidden
		/>
	</div>
</template>

<script>
import 'zuck.js/css';
import 'zuck.js/skins/snapgram';
import { httpClient } from '@shared/js/api/httpClient';

const ZUCK_LOCAL_SCRIPT_URL = '/vendor/zuck.min.js';

export default {
	name:  'MomentsStoriesPlayer',
	props: {
		items: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			zuck:                    null,
			hasNativeModal:          false,
			isReady:                 false,
			pendingStoryId:          null,
			playoutUrlsByMomentId:   {},
			videoDurationByMomentId: {},
			modalTouchStart:         null,
			hasModalSwipeHandler:    false,
		};
	},
	watch: {
		items: {
			handler() {
				this.initZuck();
			},
			deep: true,
		},
	},
	mounted() {
		this.initZuck();
		window.addEventListener('keydown', this.handleGlobalKeydown);
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.handleGlobalKeydown);
		this.removeModalSwipeDownCloseHandler();
		this.destroyZuck();
	},
	methods: {
		async buildStory(group) {
			const groupMoments = await Promise.all((group.moments || []).map(async(momentItem) => {
				const src = await this.resolveMomentVideoUrl(momentItem);
				const length = await this.resolveMomentLength(momentItem, src);

				return {
					id: momentItem.id,
					src,
					length,
				};
			}));

			const playableMoments = groupMoments.filter(moment => typeof moment.src === 'string' && moment.src.length > 0);
			if (!playableMoments.length) {
				return null;
			}

			return {
				id:          group.id,
				photo:       group.participants?.[0]?.imageUrl || '',
				name:        (group.participants || []).map(participant => participant.clubCode).join(' - '),
				lastUpdated: Math.floor(new Date(group.lastUpdatedAt).getTime() / 1000),
				createdAt:   group.createdAt,
				items:       playableMoments.map((momentItem) => ({
					id:      momentItem.id,
					type:    'video',
					length:  momentItem.length,
					src:     momentItem.src,
					preview: group.participants?.[0]?.imageUrl || '',
					time:    Math.floor(new Date(group.lastUpdatedAt).getTime() / 1000),
				})),
				photoSecondary: group.participants?.[1]?.imageUrl || '',
			};
		},
		appendStory(story) {
			if (!story || !this.zuck || typeof this.zuck.add !== 'function') {
				return;
			}

			this.zuck.add(story, true);
			if (this.pendingStoryId === story.id) {
				this.flushPendingOpen();
			}
		},
		async ensureZuckLoaded() {
			if (typeof window === 'undefined') {
				return null;
			}

			if (typeof window.Zuck === 'function') {
				return window.Zuck;
			}

			await new Promise((resolve, reject) => {
				const existingScript = document.querySelector('script[data-zuck-local-js="true"]');
				if (existingScript) {
					existingScript.addEventListener('load', resolve, { once: true });
					existingScript.addEventListener('error', reject, { once: true });
					return;
				}

				const script = document.createElement('script');
				script.src = ZUCK_LOCAL_SCRIPT_URL;
				script.async = true;
				script.dataset.zuckLocalJs = 'true';
				script.onload = resolve;
				script.onerror = reject;
				document.head.appendChild(script);
			});

			return typeof window.Zuck === 'function' ? window.Zuck : null;
		},
		initZuck() {
			if (!this.$refs.timeline) {
				return;
			}
			this.initZuckInternal();
		},
		async initZuckInternal() {
			if (!this.$refs.timeline) {
				return;
			}

			this.isReady = false;
			const Zuck = await this.ensureZuckLoaded();
			if (typeof Zuck !== 'function') {
				this.emitAvailability(false);
				this.hasNativeModal = false;
				return;
			}

			this.destroyZuck();
			this.$refs.timeline.innerHTML = '';
			const [firstGroup, ...remainingGroups] = this.items;
			const remainingStoryPromises = remainingGroups.map(group => this.buildStory(group));
			const firstStory = firstGroup ? await this.buildStory(firstGroup) : null;

			if (!firstStory) {
				const remainingStories = (await Promise.all(remainingStoryPromises)).filter(Boolean);
				if (!remainingStories.length) {
					this.emitAvailability(false);
					this.hasNativeModal = false;
					return;
				}

				const [bootstrapStory, ...restStories] = remainingStories;
				this.zuck = Zuck(this.$refs.timeline, {
					skin:           'snapgram',
					stories:        [bootstrapStory],
					backNative:     false,
					autoFullScreen: true,
					localStorage:   false,
					callbacks:      {
						onOpen: (storyId, callback) => {
							callback?.();
						},
						onView: (storyId, callback) => {
							this.decorateNativeModalProfilePhoto(storyId);
							this.applyNativeModalEventDate(storyId);
							this.ensureModalSwipeDownCloseHandler();
							callback?.();
						},
						onEnd: (storyId, callback) => {
							this.$emit('group-complete', storyId);
							callback?.();
						},
						onNextItem: (_storyId, _nextStoryId, callback) => {
							callback?.();
							this.applyNativeModalEventDateFromViewingStory();
						},
						onNavigateItem: (_storyId, _nextStoryId, callback) => {
							callback?.();
							this.applyNativeModalEventDateFromViewingStory();
						},
						onClose: (_storyId, callback) => callback?.(),
					},
				});

				this.detectNativeModal();
				this.isReady = this.hasNativeModal;
				this.emitAvailability(this.hasNativeModal);
				this.flushPendingOpen();
				restStories.forEach(story => this.appendStory(story));
				return;
			}

			this.zuck = Zuck(this.$refs.timeline, {
				skin:           'snapgram',
				stories:        [firstStory],
				backNative:     false,
				autoFullScreen: true,
				localStorage:   false,
				callbacks:      {
					onOpen: (storyId, callback) => {
						callback?.();
					},
					onView: (storyId, callback) => {
						this.decorateNativeModalProfilePhoto(storyId);
						this.applyNativeModalEventDate(storyId);
						this.ensureModalSwipeDownCloseHandler();
						callback?.();
					},
					onEnd: (storyId, callback) => {
						this.$emit('group-complete', storyId);
						callback?.();
					},
					onNextItem: (_storyId, _nextStoryId, callback) => {
						callback?.();
						this.applyNativeModalEventDateFromViewingStory();
					},
					onNavigateItem: (_storyId, _nextStoryId, callback) => {
						callback?.();
						this.applyNativeModalEventDateFromViewingStory();
					},
					onClose: (_storyId, callback) => callback?.(),
				},
			});

			this.detectNativeModal();
			this.isReady = this.hasNativeModal;
			this.emitAvailability(this.hasNativeModal);
			this.flushPendingOpen();

			const remainingStories = (await Promise.all(remainingStoryPromises)).filter(Boolean);
			remainingStories.forEach(story => this.appendStory(story));
		},
		destroyZuck() {
			this.zuck = null;
			this.hasNativeModal = false;
			this.isReady = false;
			if (this.$refs.timeline) {
				this.$refs.timeline.innerHTML = '';
			}
		},
		detectNativeModal() {
			const modalElement = document.querySelector('#zuck-modal');
			this.hasNativeModal = Boolean(modalElement?.modal?.show);
		},
		openStory(groupId) {
			if (!this.isReady || !this.hasNativeModal) {
				this.pendingStoryId = groupId;
				return;
			}

			const storyElement = Array.from(this.$refs.timeline?.querySelectorAll('.story') || [])
				.find(element => element.getAttribute('data-id') === groupId);

			if (storyElement) {
				storyElement.click();
				return;
			}

			this.pendingStoryId = groupId;
		},
		isNativeModalOpen() {
			const modalElement = document.querySelector('#zuck-modal');
			if (!modalElement) {
				return false;
			}

			const isVisible = window.getComputedStyle(modalElement).display !== 'none';
			return isVisible && Boolean(modalElement.querySelector('.story-viewer.viewing'));
		},
		handleGlobalKeydown(event) {
			if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
				return;
			}

			if (!this.isNativeModalOpen() || !this.zuck) {
				return;
			}

			event.preventDefault();
			const direction = event.key === 'ArrowRight' ? 'next' : 'previous';
			try {
				if (typeof this.zuck?.navigateItem === 'function') {
					this.zuck.navigateItem(direction, event);
					return;
				}
			} catch {
				// Fallback below.
			}

			try {
				if (typeof this.zuck?.nextItem === 'function') {
					this.zuck.nextItem(direction, event);
					return;
				}
			} catch {
				// Fallback below.
			}

			if (direction === 'next') {
				const modalElement = document.querySelector('#zuck-modal');
				modalElement?.modal?.next?.();
			}
		},
		async resolveMomentVideoUrl(momentItem) {
			if (momentItem.videoUrl) {
				return momentItem.videoUrl;
			}

			const momentId = String(momentItem.id || '');
			const eventVideoCropId = String(momentItem.eventVideoCropId || momentId);
			if (!eventVideoCropId) {
				return null;
			}

			if (this.playoutUrlsByMomentId[momentId]) {
				return this.playoutUrlsByMomentId[momentId];
			}

			try {
				const response = await httpClient.getEventVideoCropPlayout(eventVideoCropId);
				const videoUrl = response?.data?.videoUrl;
				if (!videoUrl) {
					return null;
				}

				this.playoutUrlsByMomentId = {
					...this.playoutUrlsByMomentId,
					[momentId]: videoUrl,
				};

				return videoUrl;
			} catch {
				return null;
			}
		},
		async resolveMomentLength(momentItem, videoUrl) {
			const defaultLength = 12;
			const explicitLength = Number(momentItem?.length);
			if (Number.isFinite(explicitLength) && explicitLength > 0) {
				return explicitLength;
			}

			const momentId = String(momentItem?.id || '');
			if (!momentId || !videoUrl) {
				return defaultLength;
			}

			const cachedLength = Number(this.videoDurationByMomentId[momentId]);
			if (Number.isFinite(cachedLength) && cachedLength > 0) {
				return cachedLength;
			}

			const duration = await this.getVideoDuration(videoUrl);
			const normalizedDuration = Number.isFinite(duration) && duration > 0
				? Math.max(1, Math.round(duration))
				: defaultLength;

			this.videoDurationByMomentId = {
				...this.videoDurationByMomentId,
				[momentId]: normalizedDuration,
			};

			return normalizedDuration;
		},
		async getVideoDuration(videoUrl) {
			if (!videoUrl) {
				return null;
			}

			return new Promise((resolve) => {
				const video = document.createElement('video');
				video.preload = 'metadata';
				video.muted = true;
				video.src = videoUrl;

				const cleanup = () => {
					video.removeAttribute('src');
					video.load();
				};

				video.onloadedmetadata = () => {
					const duration = Number(video.duration);
					cleanup();
					resolve(Number.isFinite(duration) ? duration : null);
				};

				video.onerror = () => {
					cleanup();
					resolve(null);
				};
			});
		},
		emitAvailability(isAvailable) {
			this.$emit('availability-changed', isAvailable);
		},
		flushPendingOpen() {
			if (!this.pendingStoryId || !this.isReady) {
				return;
			}

			const storyId = this.pendingStoryId;
			this.pendingStoryId = null;
			this.$nextTick(() => this.openStory(storyId));
		},
		decorateNativeModalProfilePhoto(storyId) {
			this.$nextTick(() => {
				const currentStory = this.items.find(item => item.id === storyId);
				if (!currentStory) {
					return;
				}

				const storyViewer = document.querySelector('#zuck-modal .story-viewer.viewing');
				const preview = storyViewer?.querySelector('.item-preview');
				if (!preview) {
					return;
				}

				preview.innerHTML = '';
				const avatarWrap = document.createElement('span');
				avatarWrap.className = 'moments-stories-player__dual-avatar';

				const firstLogo = document.createElement('img');
				firstLogo.src = currentStory.participants?.[0]?.imageUrl || currentStory.photo || '';
				firstLogo.alt = currentStory.participants?.[0]?.clubCode || '';
				firstLogo.className = 'moments-stories-player__avatar -first';
				avatarWrap.appendChild(firstLogo);

				const secondLogo = document.createElement('img');
				secondLogo.src = currentStory.participants?.[1]?.imageUrl || currentStory.photoSecondary || '';
				secondLogo.alt = currentStory.participants?.[1]?.clubCode || '';
				secondLogo.className = 'moments-stories-player__avatar -second';
				avatarWrap.appendChild(secondLogo);

				preview.appendChild(avatarWrap);
			});
		},
		applyNativeModalEventDate(storyId) {
			this.$nextTick(() => {
				const currentStory = this.items.find(item => item.id === storyId);
				const dateText = this.formatDateLabel(currentStory?.createdAt);
				if (!dateText) {
					return;
				}

				const storyViewer = document.querySelector('#zuck-modal .story-viewer.viewing');
				const leftTimeLabel = storyViewer?.querySelector('.head .left .info .time');
				const rightTimeLabel = storyViewer?.querySelector('.head .right .time');

				if (leftTimeLabel) {
					leftTimeLabel.textContent = dateText;
				}

				if (rightTimeLabel) {
					rightTimeLabel.textContent = dateText;
				}
			});
		},
		applyNativeModalEventDateFromViewingStory() {
			const storyViewer = document.querySelector('#zuck-modal .story-viewer.viewing');
			const viewingStoryId = storyViewer?.getAttribute('data-story-id');
			if (!viewingStoryId) {
				return;
			}

			this.applyNativeModalEventDate(viewingStoryId);
		},
		formatDateLabel(dateValue) {
			if (!dateValue) {
				return '';
			}

			const parsedDate = new Date(dateValue);
			if (Number.isNaN(parsedDate.getTime())) {
				return '';
			}

			return new Intl.DateTimeFormat(undefined, {
				year:  'numeric',
				month: '2-digit',
				day:   '2-digit',
			}).format(parsedDate);
		},
		ensureModalSwipeDownCloseHandler() {
			if (this.hasModalSwipeHandler) {
				return;
			}

			const modalElement = document.querySelector('#zuck-modal');
			if (!modalElement) {
				return;
			}

			modalElement.addEventListener('touchstart', this.onModalTouchStart, { passive: true });
			modalElement.addEventListener('touchend', this.onModalTouchEnd, { passive: true });
			this.hasModalSwipeHandler = true;
		},
		removeModalSwipeDownCloseHandler() {
			if (!this.hasModalSwipeHandler) {
				return;
			}

			const modalElement = document.querySelector('#zuck-modal');
			if (modalElement) {
				modalElement.removeEventListener('touchstart', this.onModalTouchStart);
				modalElement.removeEventListener('touchend', this.onModalTouchEnd);
			}

			this.hasModalSwipeHandler = false;
		},
		onModalTouchStart(event) {
			if (!this.isMobileViewport()) {
				return;
			}

			const touch = event.changedTouches?.[0];
			if (!touch) {
				return;
			}

			this.modalTouchStart = {
				x: touch.clientX,
				y: touch.clientY,
			};
		},
		onModalTouchEnd(event) {
			if (!this.isMobileViewport() || !this.modalTouchStart) {
				return;
			}

			const touch = event.changedTouches?.[0];
			if (!touch) {
				this.modalTouchStart = null;
				return;
			}

			const deltaX = touch.clientX - this.modalTouchStart.x;
			const deltaY = touch.clientY - this.modalTouchStart.y;
			this.modalTouchStart = null;

			const isSwipeDown = deltaY > 80 && Math.abs(deltaY) > Math.abs(deltaX) * 1.2;
			if (!isSwipeDown) {
				return;
			}

			const modalElement = document.querySelector('#zuck-modal');
			modalElement?.modal?.close?.();
		},
		isMobileViewport() {
			if (typeof window === 'undefined') {
				return false;
			}

			return window.matchMedia('(max-width: 1024px)').matches;
		},
	},
};
</script>

<style lang="scss">
.moments-stories-player__timeline {
	display: none !important; // stylelint-disable-line
}

// Story Logo
/* stylelint-disable */
#zuck-modal-content {
	.story-viewer {
		.head {
			text-shadow: none !important;

			&::before {
				content: '';
				position: absolute;
				left: 0;
				top: 0;
				width: 100%;
				height: 112px;
				background: linear-gradient(180deg, rgba(0, 0, 0, 0.42) 0%, rgba(0, 0, 0, 0) 100%);
				pointer-events: none;
				z-index: 0;
			}

			.left,
			.right {
				position: relative;
				z-index: 1;
			}

			.item-preview {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				overflow: visible;
				width: 50px !important;
			}

			.left {
				.back {
					margin-top: -4px;
				}
			}

			.right {
				.close {
					line-height: 1;
					padding-right: 10px;
				}
			}
		}

		.info {
			.name {
				text-shadow: none !important;
			}
		}
	}
}

#zuck-modal.with-effects {
	transform-origin: center center !important;
}

@media (max-width: 1024px) {
	#zuck-modal-content .story-viewer .head .left > .back {
		display: none !important;
	}

	#zuck-modal-content .story-viewer .head .right .close {
		display: block !important;
	}

	#zuck-modal-content .story-viewer .head .right .time {
		display: none !important;
	}
}
/* stylelint-enable */

.moments-stories-player__dual-avatar {
	position: relative;
	display: inline-flex;
	width: 50px !important; // stylelint-disable-line
	height: 32px;
}

.moments-stories-player__avatar {
	position: absolute;
	width: 32px !important; // stylelint-disable-line
	height: 32px !important; // stylelint-disable-line
	object-fit: cover;

	&.-first {
		left: 0;
		top: 2px;
		z-index: 2;
	}

	&.-second {
		right: 0;
		top: 2px;
		z-index: 1;
	}
}
</style>
